import { getScoreHeader } from '@sportal365/smp-widget-utils';
import { ConfigContext, MatchScore, SMPFlex } from '@velinsi/smp-widgets-ui';
import { useCallback, useContext, useMemo } from 'react';
import { MatchTypes, Winner } from '../../../../../../packages/constants/tennis';
import { HeaderLabels, INormalizedTennisMatch } from '../../../../../../packages/types';
import { CANCELLED, FINISHED, LIVE, NOT_STARTED } from '../../../constants/statusTypes';

export interface IEventProps {
  match?: INormalizedTennisMatch;
  participantType?: string;
  handleEventClick?: (id: string | number) => void;
  isMobile?: boolean;
  width: number;
}

function Score({ match, handleEventClick, isMobile, width }: IEventProps): JSX.Element {
  const { dataLabels } = useContext(ConfigContext);
  const matchLink = useMemo(() => handleEventClick && match?.matchLink, [handleEventClick, match?.matchLink]);
  const matchUrlTarget = useMemo(() => handleEventClick && match?.urlTarget, [handleEventClick, match?.urlTarget]);

  const matchScoresVariant = useCallback(
    (variant: 'home' | 'away' | 'both') => {
      if (!match) {
        return null;
      }

      const commonProps = {
        eventId: match?.id,
        handleEventClick,
        isMobile,
        matchLink,
        matchUrlTarget,
      };
      const matchScoresToDisplay: JSX.Element[] = [];

      match.setScores.forEach((setScore, i) => {
        if (!setScore || (!setScore.home.score.value && !setScore.away.score.value)) {
          return;
        }

        let homeProps = {};
        let awayProps = {};

        if (['home', 'both'].includes(variant)) {
          homeProps = {
            homeTeamScore: setScore.home?.score?.value,
            homeTeamTiebreak: setScore.home?.tiebreakScore?.value,
            homeTeamWeight: setScore.home?.score?.winner === Winner.YES ? '700' : '400',
          };
        }

        if (['away', 'both'].includes(variant)) {
          awayProps = {
            awayTeamScore: setScore.away?.score?.value,
            awayTeamTiebreak: setScore.away?.tiebreakScore?.value,
            awayTeamWeight: setScore.away?.score?.winner === Winner.YES ? '700' : '400',
          };
        }

        const props = {
          ...commonProps,
          ...homeProps,
          ...awayProps,
        };

        let header: string | undefined = (i + 1).toString();

        if (variant === 'away' && isMobile && match.type === MatchTypes.DOUBLE) {
          header = undefined;
        }

        matchScoresToDisplay.push(<MatchScore key={variant + (i + 1)} {...props} header={header} width={width} />);
      });

      return <>{matchScoresToDisplay}</>;
    },
    [match, handleEventClick, isMobile, matchLink, matchUrlTarget, width],
  );

  const hasGameScore = match?.gameScore !== undefined && match.gameScore.home.score.value !== '' && match.gameScore.away.score.value !== '';

  const hasRunningScore =
    match?.runningScore !== undefined &&
    match.matchStatus.type === FINISHED &&
    match.runningScore.home.score.value !== '' &&
    match.runningScore.away.score.value !== '';

  return (
    <>
      {isMobile && match?.type === MatchTypes.DOUBLE ? (
        <SMPFlex variant='column'>
          <SMPFlex grow='1' align='end' divider='bottom' variant='tennisDoubleMatchMobileScore'>
            {(hasGameScore || match.matchStatus.type === LIVE) && (
              <MatchScore
                eventId={match?.id}
                handleEventClick={handleEventClick}
                isMobile={isMobile}
                matchLink={matchLink}
                matchUrlTarget={matchUrlTarget}
                homeTeamScore={match?.gameScore?.home.score?.value}
                homeTeamTiebreak={match?.gameScore?.home?.tiebreakScore?.value}
                variant='accessibilityPrimaryColor'
                header={getScoreHeader(dataLabels, HeaderLabels.POINTS)}
                width={width}
              />
            )}
            {matchScoresVariant('home')}
            {(hasRunningScore || [CANCELLED, NOT_STARTED].includes(match?.matchStatus.type)) && (
              <MatchScore
                isMobile={isMobile}
                eventId={match?.id}
                handleEventClick={handleEventClick}
                matchLink={matchLink}
                matchUrlTarget={matchUrlTarget}
                homeTeamScore={match?.runningScore?.home.score?.value}
                homeTeamTiebreak={match?.runningScore?.home.tiebreakScore.value}
                variant={hasRunningScore ? 'accessibilityPrimaryColor' : undefined}
                header={getScoreHeader(dataLabels, HeaderLabels.SCORE)}
                width={width}
              />
            )}
          </SMPFlex>
          <SMPFlex grow='1' align='start' variant='tennisDoubleMatchMobileScore'>
            {(hasGameScore || match.matchStatus.type === LIVE) && (
              <MatchScore
                eventId={match?.id}
                handleEventClick={handleEventClick}
                isMobile={isMobile}
                matchLink={matchLink}
                matchUrlTarget={matchUrlTarget}
                awayTeamScore={match?.gameScore?.away.score?.value}
                awayTeamTiebreak={match?.gameScore?.away?.tiebreakScore?.value}
                variant='accessibilityPrimaryColor'
                width={width}
              />
            )}
            {matchScoresVariant('away')}
            {(hasRunningScore || [CANCELLED, NOT_STARTED].includes(match?.matchStatus.type)) && (
              <MatchScore
                isMobile={isMobile}
                eventId={match?.id}
                handleEventClick={handleEventClick}
                matchLink={matchLink}
                matchUrlTarget={matchUrlTarget}
                awayTeamScore={match?.runningScore?.away.score?.value}
                awayTeamTiebreak={match?.runningScore?.away.tiebreakScore.value}
                variant={hasRunningScore ? 'accessibilityPrimaryColor' : undefined}
                width={width}
              />
            )}
          </SMPFlex>
        </SMPFlex>
      ) : (
        <SMPFlex>
          {hasGameScore && (
            <MatchScore
              isMobile={isMobile}
              eventId={match?.id}
              handleEventClick={handleEventClick}
              matchLink={matchLink}
              matchUrlTarget={matchUrlTarget}
              homeTeamScore={match?.gameScore?.home.score?.value}
              awayTeamScore={match?.gameScore?.away.score?.value}
              homeTeamTiebreak={match?.gameScore?.home?.tiebreakScore?.value}
              awayTeamTiebreak={match?.gameScore?.away?.tiebreakScore?.value}
              variant='accessibilityPrimaryColor'
              header={getScoreHeader(dataLabels, HeaderLabels.POINTS)}
              width={width}
            />
          )}
          {matchScoresVariant('both')}
          {(hasRunningScore || [CANCELLED, NOT_STARTED].includes(match?.matchStatus.type as string)) && (
            <MatchScore
              isMobile={isMobile}
              eventId={match?.id}
              handleEventClick={handleEventClick}
              matchLink={matchLink}
              matchUrlTarget={matchUrlTarget}
              homeTeamScore={match?.runningScore?.home.score?.value}
              awayTeamScore={match?.runningScore?.away.score?.value}
              homeTeamTiebreak={match?.runningScore?.home.tiebreakScore.value}
              awayTeamTiebreak={match?.runningScore?.away.tiebreakScore.value}
              variant={hasRunningScore ? 'accessibilityPrimaryColor' : undefined}
              header={getScoreHeader(dataLabels, HeaderLabels.SCORE)}
              width={width}
            />
          )}
        </SMPFlex>
      )}
    </>
  );
}

export default Score;
